import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import type { SocketNamespace } from '@/types';
import { socketService } from '@/services/SocketService';
import { useAuthStore } from '@/store/authStore';

interface SocketContextValue {
  sockets: Map<SocketNamespace, Socket>;
  connected: Record<SocketNamespace, boolean>;
  loading: boolean;
  getSocket: (namespace: SocketNamespace) => Socket | undefined;
  isConnected: (namespace: SocketNamespace) => boolean;
}

const SocketContext = createContext<SocketContextValue | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

const NAMESPACES: SocketNamespace[] = [
  'client',
  'tournaments',
  'ladder',
  'gather',
  'lan',
  'teams',
  'matches',
  'challenges',
  'friends',
  'chat',
];

export function SocketProvider({ children }: SocketProviderProps) {
  const [sockets, setSockets] = useState<Map<SocketNamespace, Socket>>(new Map());
  const [connected, setConnected] = useState<Record<SocketNamespace, boolean>>({} as Record<SocketNamespace, boolean>);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    cancelledRef.current = false;

    async function connectAll() {
      // Connect all namespaces in parallel and update state incrementally
      // so each socket becomes available as soon as it connects
      const promises = NAMESPACES.map(async (ns) => {
        if (cancelledRef.current) return;

        try {
          const socket = await socketService.connect(ns);

          if (cancelledRef.current) {
            socketService.disconnect(ns);
            return;
          }

          socket.on('connect', () => {
            setConnected((prev) => ({ ...prev, [ns]: true }));
          });

          socket.on('disconnect', () => {
            setConnected((prev) => ({ ...prev, [ns]: false }));
          });

          socket.on('connect_error', (error) => {
            console.error(`Connection error on /${ns}:`, error.message);
            setConnected((prev) => ({ ...prev, [ns]: false }));
          });

          // Update state immediately so components can use this socket right away
          if (!cancelledRef.current) {
            setSockets((prev) => {
              const next = new Map(prev);
              next.set(ns, socket);
              return next;
            });
            setConnected((prev) => ({ ...prev, [ns]: socket.connected }));
          }
        } catch (error) {
          console.error(`Failed to connect to /${ns}:`, error);
          setConnected((prev) => ({ ...prev, [ns]: false }));
        }
      });

      await Promise.allSettled(promises);

      if (!cancelledRef.current) {
        setLoading(false);
      }
    }

    connectAll();

    return () => {
      cancelledRef.current = true;
      NAMESPACES.forEach((ns) => {
        try {
          socketService.disconnect(ns);
        } catch (error) {
          console.error(`Error disconnecting from /${ns}:`, error);
        }
      });
    };
  }, [isAuthenticated]);

  const getSocket = (namespace: SocketNamespace): Socket | undefined => {
    return sockets.get(namespace);
  };

  const isConnectedToNamespace = (namespace: SocketNamespace): boolean => {
    return connected[namespace] ?? false;
  };

  return (
    <SocketContext.Provider
      value={{
        sockets,
        connected,
        loading,
        getSocket,
        isConnected: isConnectedToNamespace,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export const useSockets = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSockets must be used within SocketProvider');
  }
  return context;
};

export const useNamespaceSocket = (namespace: SocketNamespace) => {
  const { getSocket, isConnected } = useSockets();
  return {
    socket: getSocket(namespace),
    isConnected: isConnected(namespace),
  };
};
